import React, { useState, useRef } from "react";
import { connect } from "react-redux";
import { motion } from "framer-motion";
import styles from "./NuevoProducto.module.scss";
import {
  openMainModal,
  closeMainModal,
  setPortfolio,
} from "../../../../../store/actions";
import { X } from "lucide-react";
import NuevoProductoModal from "./NuevoProductoModal.tsx";
import ModalOverlay from "../../../../UI/ModalOverlay/ModalOverlay.tsx";

interface PortfolioFormValues {
  name: string;
  startDate: string;
  endDate: string;
  description: string;
  zone: string;
  type: string;
  priceUnit: string;
  price: string;
  currencyType: string;
}

interface NuevoProductoProps {
  setPortfolio: (values: PortfolioFormValues, images: File[], context: string) => void;
  openMainModal: (config: any) => void;
  cancel: () => void;
  closeMainModal: () => void;
}

const modalVariants = {
  hidden: { opacity: 0, scale: 1.1, y: 20 },
  visible: {
    opacity: 1,
    scale: 1.0,
    y: 0,
    transition: { delay: 0.1, duration: 0.3, ease: "easeOut" },
  },
};

const NuevoProducto: React.FC<NuevoProductoProps> = ({
  setPortfolio,
  openMainModal,
  cancel,
  closeMainModal,
}) => {
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [formData, setFormData] = useState<PortfolioFormValues>({
    name: "",
    startDate: "",
    endDate: "",
    description: "",
    zone: "",
    type: "",
    priceUnit: "",
    price: "",
    currencyType: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const contentRef = useRef<HTMLDivElement>(null);

  const validateForm = (): Record<string, string> => {
    const nextErrors: Record<string, string> = {};
    if (!formData.name.trim()) nextErrors.name = "Este campo es obligatorio";
    if (!formData.description.trim()) nextErrors.description = "Este campo es obligatorio";
    if (!formData.startDate) nextErrors.startDate = "Este campo es obligatorio";
    if (!formData.endDate) nextErrors.endDate = "Este campo es obligatorio";
    if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
      nextErrors.endDate = "La fecha fin debe ser posterior a la fecha de inicio";
    }
    if (!formData.zone.trim()) nextErrors.zone = "Este campo es obligatorio";
    if (imageFiles.length === 0) nextErrors.images = "Debes agregar al menos una imagen";
    if (!formData.type) nextErrors.type = "Este campo es obligatorio";
    if (!formData.currencyType) nextErrors.currencyType = "Este campo es obligatorio";
    if (!formData.priceUnit) nextErrors.priceUnit = "Este campo es obligatorio";
    if (!formData.price.trim()) nextErrors.price = "Este campo es obligatorio";
    return nextErrors;
  };

  const handleFieldChange = (
    field: keyof PortfolioFormValues,
    value: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleImageChange = (newImages: File[]) => {
    setImageFiles(newImages);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.images;
      return next;
    });
  };

  const createHandler = () => {
    const nextErrors = validateForm();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      const firstErrorKey = Object.keys(nextErrors)[0];
      const target = contentRef.current?.querySelector(`[data-field="${firstErrorKey}"]`);
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    openMainModal({
      title: `¿Estas seguro que deseas crear el ${
        formData.type === "producto" ? "producto" : "servicio"
      } en edify?`,
      iconType: "warning",
      body: "Una vez creado será publicado en edify y podrá ser visto por toda la comunidad.",
      isSuccess: false,
      confirmHandler: () => {
        cancel();
        setPortfolio(formData, imageFiles, "perfil");
        closeMainModal();
      },
    });
  };

  return (
    <ModalOverlay isOpen={true} onClose={cancel}>
      <motion.div
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
        className={styles.newPortfolioModal}
      >
        <motion.div
          initial={{ height: "0" }}
          animate={{ height: "auto" }}
          exit={{ height: "0" }}
          transition={{ damping: 5, height: { duration: 0.5 } }}
          className={styles.container}
        >
          <header className={styles.container_header}>
            <div className={styles.container_header_txt}>
              Crear nuevo producto o servicio
            </div>
            <div className={styles.container_header_cancel} onClick={cancel}>
              <X size={24} />
            </div>
          </header>
          <div ref={contentRef} style={{ overflow: "auto", flex: 1 }}>
            <NuevoProductoModal
              formData={formData}
              onFieldChange={handleFieldChange}
              onImageChange={handleImageChange}
              imageFiles={imageFiles}
              errors={errors}
            />
          </div>
          <div className={styles.container_footer}>
            <button className={styles.container_cancel} onClick={cancel}>
              Cancelar
            </button>
            <button
              className={`${styles.container_submit} ${
                Object.keys(errors).length > 0 ? styles.disabled : ""
              }`}
              disabled={Object.keys(errors).length > 0}
              onClick={createHandler}
            >
              {Object.keys(errors).length > 0 ? "Faltan datos" : "Publicar en mi perfil"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </ModalOverlay>
  );
};

export default connect(null, {
  openMainModal,
  setPortfolio,
  closeMainModal,
})(NuevoProducto);
